import type { UserRecord } from '../../api/admin';

export type CareerWorkType = 'full_time' | 'part_time' | 'contract';
export type CandidateCustomerType = 'individual' | 'business';
export type CandidateSource = 'job_board' | 'manual_admin';
export type JobListingStatus = 'draft' | 'published' | 'paused' | 'closed_filled' | 'deleted';
export type CandidateApplicationStatus =
  | 'application_received'
  | 'in_review'
  | 'rejected'
  | 'hired';
export type CandidateInterviewStatus =
  | 'not_scheduled'
  | 'scheduled'
  | 'in_progress'
  | 'interview_complete';

export interface JobApplicationPhase {
  id: string;
  name: string;
  order: number;
  interviewer: string;
  notes: string;
  scorecard: number | null;
}

export interface CareerJob {
  id: string;
  slug: string;
  requisitionId: string;
  title: string;
  team: string;
  brainNodeId?: string;
  location: string;
  workType: CareerWorkType;
  summary: string;
  description: string;
  responsibilities: string[];
  requirements: string[];
  published: boolean;
  listingStatus?: JobListingStatus;
  applicationPhases?: JobApplicationPhase[];
  hiringManager?: string;
  targetHireCount?: number;
  compensation?: string;
  createdAt: number;
  updatedAt: number;
  source: 'seed' | 'admin' | 'remote';
}

export interface CareerJobDraft {
  title: string;
  requisitionId: string;
  team: string;
  brainNodeId?: string;
  location: string;
  workType: CareerWorkType;
  summary: string;
  description: string;
  responsibilities: string[];
  requirements: string[];
  published: boolean;
  listingStatus?: JobListingStatus;
  applicationPhases?: JobApplicationPhase[];
  hiringManager?: string;
  targetHireCount?: number;
  compensation?: string;
}

export interface CandidateApplicationRecord {
  id: string;
  role: 'candidate';
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  contactPhone: string;
  userType: CandidateCustomerType;
  businessName: string;
  businessWebsite: string;
  businessAddress: string;
  candidateCity: string;
  candidateState: string;
  candidatePostalCode: string;
  backgroundCheckAcknowledged: boolean;
  pointOfContact: string;
  bio: string;
  linkedInUrl: string;
  portfolioUrl: string;
  coverLetterText: string;
  requisitionId: string;
  jobId: string;
  jobTitle: string;
  resumeFileName: string;
  coverLetterFileName: string;
  resumeStoragePath: string;
  coverLetterStoragePath: string;
  resumeDownloadUrl: string;
  coverLetterDownloadUrl: string;
  source: CandidateSource;
  status: CandidateApplicationStatus;
  currentPhaseId: string;
  reviewer: string;
  phaseNotes: string;
  phaseScorecard: number | null;
  stageReviewerScores?: Record<string, Record<string, number>>;
  interviewStatus?: CandidateInterviewStatus | string;
  interviewScheduledAt?: number | null;
  interviewCompletedAt?: number | null;
  latestInterviewEventId?: string | null;
  interviewMeetingUrl?: string;
  offerAccepted?: boolean;
  agentUserId?: string | null;
  applicationUpdatedAt: number;
  createdAt: number;
  updatedAt: number;
}

export interface CandidateApplicationDraft {
  firstName: string;
  lastName: string;
  email: string;
  contactPhone?: string;
  userType: CandidateCustomerType;
  businessName?: string;
  businessWebsite?: string;
  businessAddress?: string;
  candidateCity?: string;
  candidateState?: string;
  candidatePostalCode?: string;
  backgroundCheckAcknowledged?: boolean;
  pointOfContact?: string;
  bio?: string;
  linkedInUrl?: string;
  portfolioUrl?: string;
  coverLetterText?: string;
  requisitionId: string;
  jobId: string;
  jobTitle: string;
  resumeFileName?: string;
  coverLetterFileName?: string;
  resumeStoragePath?: string;
  coverLetterStoragePath?: string;
  resumeDownloadUrl?: string;
  coverLetterDownloadUrl?: string;
  source?: CandidateSource;
  status?: CandidateApplicationStatus;
  currentPhaseId?: string;
  reviewer?: string;
  phaseNotes?: string;
  phaseScorecard?: number | null;
  stageReviewerScores?: Record<string, Record<string, number>>;
  interviewStatus?: CandidateInterviewStatus | string;
  interviewScheduledAt?: number | null;
  interviewCompletedAt?: number | null;
  latestInterviewEventId?: string | null;
  interviewMeetingUrl?: string;
  offerAccepted?: boolean;
  agentUserId?: string | null;
}

const LOCAL_JOBS_KEY = 'cybard.careers.jobs.v1';
const LOCAL_CANDIDATES_KEY = 'cybard.careers.candidates.v1';

function canUseStorage(): boolean {
  return typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';
}

function readStorageArray<T>(key: string): T[] {
  if (!canUseStorage()) return [];
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

function writeStorageArray<T>(key: string, value: T[]): void {
  if (!canUseStorage()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // no-op
  }
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 80);
}

function uniqueSlug(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}-${i}`)) i += 1;
  return `${base}-${i}`;
}

function normalizeList(value: string[]): string[] {
  return value.map((item) => item.trim()).filter(Boolean);
}

function normalizeTargetHireCount(
  value: unknown,
  fallback = 1,
): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  const rounded = Math.round(value);
  if (rounded < 1) return 1;
  if (rounded > 99) return 99;
  return rounded;
}

const DEFAULT_REVIEW_STAGE_NAME = 'Review application';

function normalizeScoreTarget(value: unknown): number | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const rounded = Math.round(value);
  if (rounded < 1) return 1;
  if (rounded > 10) return 10;
  return rounded;
}

export function createDefaultJobApplicationPhases(seed: string): JobApplicationPhase[] {
  const safeSeed = slugify(seed || 'job');
  return [
    {
      id: `phase_review_${safeSeed}`,
      name: DEFAULT_REVIEW_STAGE_NAME,
      order: 1,
      interviewer: '',
      notes: '',
      scorecard: null,
    },
  ];
}

function normalizeApplicationPhases(
  phases: JobApplicationPhase[] | undefined,
  seed: string,
): JobApplicationPhase[] {
  const source =
    Array.isArray(phases) && phases.length > 0
      ? phases
      : createDefaultJobApplicationPhases(seed);

  const mapped = source
    .map((phase, index) => ({
      id: phase.id?.trim() || `phase_${index + 1}_${slugify(seed)}`,
      name:
        index === 0
          ? DEFAULT_REVIEW_STAGE_NAME
          : phase.name?.trim() || `Stage ${index + 1}`,
      order: Number.isFinite(phase.order) ? Number(phase.order) : index + 1,
      interviewer: phase.interviewer?.trim() || '',
      notes: phase.notes?.trim() || '',
      scorecard: normalizeScoreTarget(phase.scorecard),
    }))
    .sort((a, b) => a.order - b.order)
    .map((phase, index) => ({ ...phase, order: index + 1 }));

  if (!mapped.length) return createDefaultJobApplicationPhases(seed);

  const [first, ...rest] = mapped;
  return [
    {
      ...first,
      id: first.id?.trim() || `phase_review_${slugify(seed)}`,
      name: DEFAULT_REVIEW_STAGE_NAME,
      order: 1,
    },
    ...rest.map((phase, index) => ({
      ...phase,
      name: phase.name?.trim() || `Stage ${index + 2}`,
      order: index + 2,
    })),
  ];
}

export function getCareerJobListingStatus(
  job: Pick<CareerJob, 'listingStatus' | 'published'>,
): JobListingStatus {
  if (job.listingStatus === 'draft') return 'draft';
  if (job.listingStatus === 'paused') return 'paused';
  if (job.listingStatus === 'closed_filled') return 'closed_filled';
  if (job.listingStatus === 'deleted') return 'deleted';
  if (job.listingStatus === 'published') return 'published';
  return job.published ? 'published' : 'draft';
}

export function getCareerJobApplicationPhases(job: CareerJob): JobApplicationPhase[] {
  return normalizeApplicationPhases(job.applicationPhases, job.id || job.slug || job.title);
}

export function normalizeCandidateApplicationStatus(
  status: CandidateApplicationStatus | string | null | undefined,
): CandidateApplicationStatus {
  if (status === 'in_review') return 'in_review';
  if (status === 'rejected') return 'rejected';
  if (status === 'hired') return 'hired';
  return 'application_received';
}

function isPublicJob(job: CareerJob): boolean {
  return Boolean(job.published) && getCareerJobListingStatus(job) === 'published';
}

function careerJobMergeKey(job: Pick<CareerJob, 'id' | 'slug' | 'requisitionId'>): string {
  const requisitionId = job.requisitionId.trim().toLowerCase();
  if (requisitionId) return `req:${requisitionId}`;
  const slug = job.slug.trim().toLowerCase();
  if (slug) return `slug:${slug}`;
  return `id:${job.id.trim().toLowerCase()}`;
}

const DEFAULT_JOBS: CareerJob[] = [
  {
    id: 'job_digital_marketer',
    slug: 'digital-marketer',
    requisitionId: 'CYB-DM-001',
    title: 'Digital Marketer',
    team: 'Growth',
    location: 'Remote - US',
    workType: 'full_time',
    summary:
      'Own campaign strategy, content distribution, and performance insights across paid and organic channels.',
    description:
      'Cybard is looking for a digital marketer who can blend creative experimentation with disciplined analytics. You will own campaign planning, launch, testing, and reporting across our growth surfaces.',
    responsibilities: [
      'Build and manage multi-channel campaign calendars.',
      'Plan and execute paid social and search tests.',
      'Work with design and copy teams on high-converting assets.',
      'Track pipeline impact and optimize campaigns weekly.',
    ],
    requirements: [
      '3+ years in digital marketing or growth.',
      'Strong paid media and funnel analytics experience.',
      'Comfortable with attribution, tracking, and reporting stacks.',
      'Excellent communication and execution discipline.',
    ],
    published: true,
    listingStatus: 'published',
    applicationPhases: createDefaultJobApplicationPhases('job_digital_marketer'),
    hiringManager: '',
    targetHireCount: 1,
    compensation: '',
    createdAt: 1738368000000,
    updatedAt: 1738368000000,
    source: 'seed',
  },
  {
    id: 'job_business_developer',
    slug: 'business-developer',
    requisitionId: 'CYB-BD-002',
    title: 'Business Developer',
    team: 'Revenue',
    location: 'Remote - US',
    workType: 'full_time',
    summary:
      'Develop outbound and partner channels, qualify opportunities, and progress accounts into active clients.',
    description:
      'We need a business developer who thrives in high-velocity relationship building and can bring structure to outreach, qualification, and pipeline progression.',
    responsibilities: [
      'Own outbound prospecting and account targeting.',
      'Qualify inbound and partner leads against ICP criteria.',
      'Run discovery and handoff to strategy and delivery teams.',
      'Maintain CRM hygiene and forecast updates.',
    ],
    requirements: [
      '3+ years in B2B sales, partnerships, or business development.',
      'Strong communication and objection handling.',
      'CRM and pipeline management proficiency.',
      'Comfortable in a fast-moving startup environment.',
    ],
    published: true,
    listingStatus: 'published',
    applicationPhases: createDefaultJobApplicationPhases('job_business_developer'),
    hiringManager: '',
    targetHireCount: 1,
    compensation: '',
    createdAt: 1738368000000,
    updatedAt: 1738368000000,
    source: 'seed',
  },
  {
    id: 'job_project_manager',
    slug: 'project-manager',
    requisitionId: 'CYB-PM-003',
    title: 'Project Manager',
    team: 'Delivery',
    location: 'Remote - US',
    workType: 'full_time',
    summary:
      'Lead cross-functional delivery, keep scope and timelines aligned, and ensure high-quality project execution.',
    description:
      'Cybard is hiring a project manager who can coordinate internal teams, external clients, and key milestones while preserving delivery quality and speed.',
    responsibilities: [
      'Manage project plans, sprints, and execution checkpoints.',
      'Facilitate communication between clients and internal teams.',
      'Track risks, blockers, and timeline adjustments.',
      'Maintain delivery documentation and status reporting.',
    ],
    requirements: [
      '3+ years managing digital projects.',
      'Experience with agile or hybrid delivery workflows.',
      'Strong written and verbal communication.',
      'Detail-oriented, proactive, and outcome-focused.',
    ],
    published: true,
    listingStatus: 'published',
    applicationPhases: createDefaultJobApplicationPhases('job_project_manager'),
    hiringManager: '',
    targetHireCount: 1,
    compensation: '',
    createdAt: 1738368000000,
    updatedAt: 1738368000000,
    source: 'seed',
  },
];

export function getDefaultCareerJobs(): CareerJob[] {
  return DEFAULT_JOBS.map((job) => ({
    ...job,
    listingStatus: getCareerJobListingStatus(job),
    applicationPhases: getCareerJobApplicationPhases(job),
    hiringManager: (job.hiringManager ?? '').trim(),
    targetHireCount: normalizeTargetHireCount(job.targetHireCount, 1),
    compensation: (job.compensation ?? '').trim(),
  }));
}

export function getLocalCareerJobs(): CareerJob[] {
  return readStorageArray<CareerJob>(LOCAL_JOBS_KEY)
    .filter((job) => Boolean(job?.id && job?.title))
    .map((job) => ({
      ...job,
      listingStatus: getCareerJobListingStatus(job),
      applicationPhases: getCareerJobApplicationPhases(job),
      hiringManager: (job.hiringManager ?? '').trim(),
      targetHireCount: normalizeTargetHireCount(job.targetHireCount, 1),
      compensation: (job.compensation ?? '').trim(),
    }));
}

export function saveLocalCareerJobs(jobs: CareerJob[]): void {
  writeStorageArray(LOCAL_JOBS_KEY, jobs);
}

export function mergeCareerJobs(remote: CareerJob[], local: CareerJob[]): CareerJob[] {
  const merged = new Map<string, CareerJob>();
  const suppressedByLocal = new Set<string>();

  // Local status is authoritative for public visibility.
  // If a local record exists and is not public (paused/draft/closed/deleted),
  // suppress that slug from default/remote public listings.
  for (const job of local) {
    if (!isPublicJob(job)) {
      suppressedByLocal.add(careerJobMergeKey(job));
    }
  }

  for (const job of getDefaultCareerJobs()) {
    const key = careerJobMergeKey(job);
    if (suppressedByLocal.has(key)) continue;
    if (!isPublicJob(job)) continue;
    merged.set(key, job);
  }

  for (const job of remote) {
    const key = careerJobMergeKey(job);
    if (suppressedByLocal.has(key)) continue;
    if (!isPublicJob(job)) continue;
    merged.set(key, job);
  }

  for (const job of local) {
    const key = careerJobMergeKey(job);
    if (!isPublicJob(job)) continue;
    merged.set(key, job);
  }

  return Array.from(merged.values()).sort((a, b) => b.updatedAt - a.updatedAt);
}

export function getPublishedCareerJobs(): CareerJob[] {
  return mergeCareerJobs([], getLocalCareerJobs());
}

export function getAllCareerJobs(): CareerJob[] {
  const merged = new Map<string, CareerJob>();
  for (const job of getDefaultCareerJobs()) merged.set(job.slug, job);
  for (const job of getLocalCareerJobs()) merged.set(job.slug, job);
  return Array.from(merged.values()).sort((a, b) => b.updatedAt - a.updatedAt);
}

export function createLocalCareerJob(draft: CareerJobDraft): CareerJob {
  const now = Date.now();
  const current = getLocalCareerJobs();
  const slugSet = new Set([
    ...getDefaultCareerJobs().map((job) => job.slug),
    ...current.map((job) => job.slug),
  ]);

  const baseSlug = slugify(draft.title || draft.requisitionId || `role-${now}`);
  const slug = uniqueSlug(baseSlug || `role-${now}`, slugSet);
  const id = `job_${slug}_${now}`;
  const listingStatus = draft.listingStatus ?? (draft.published ? 'published' : 'draft');
  const published =
    listingStatus === 'published'
      ? true
      : listingStatus === 'paused' || listingStatus === 'closed_filled' || listingStatus === 'deleted'
      ? false
      : Boolean(draft.published);

  const job: CareerJob = {
    id,
    slug,
    requisitionId: draft.requisitionId.trim(),
    title: draft.title.trim(),
    team: draft.team.trim(),
    brainNodeId: (draft.brainNodeId ?? '').trim() || undefined,
    location: draft.location.trim(),
    workType: draft.workType,
    summary: draft.summary.trim(),
    description: draft.description.trim(),
    responsibilities: normalizeList(draft.responsibilities),
    requirements: normalizeList(draft.requirements),
    published,
    listingStatus,
    applicationPhases: normalizeApplicationPhases(draft.applicationPhases, id),
    hiringManager: (draft.hiringManager ?? '').trim(),
    targetHireCount: normalizeTargetHireCount(draft.targetHireCount, 1),
    compensation: (draft.compensation ?? '').trim(),
    createdAt: now,
    updatedAt: now,
    source: 'admin',
  };

  saveLocalCareerJobs([job, ...current]);
  return job;
}

type CareerJobUpdatePatch = Partial<
  Pick<
    CareerJob,
    | 'title'
    | 'requisitionId'
    | 'team'
    | 'brainNodeId'
    | 'location'
    | 'workType'
    | 'summary'
    | 'description'
    | 'responsibilities'
    | 'requirements'
    | 'published'
    | 'listingStatus'
    | 'applicationPhases'
    | 'hiringManager'
    | 'targetHireCount'
    | 'compensation'
  >
>;

export function updateLocalCareerJobRecord(
  jobId: string,
  patch: CareerJobUpdatePatch,
): CareerJob | null {
  const current = getLocalCareerJobs();
  let idx = current.findIndex((job) => job.id === jobId);
  let existing = idx >= 0 ? current[idx] : null;

  if (!existing) {
    const seed = getDefaultCareerJobs().find((job) => job.id === jobId) ?? null;
    if (!seed) return null;
    existing = { ...seed, source: 'admin' };
    current.unshift(existing);
    idx = 0;
  }

  const nextStatus = patch.listingStatus ?? getCareerJobListingStatus(existing);
  const nextPublished =
    patch.published ??
    (nextStatus === 'published'
      ? true
      : nextStatus === 'paused' || nextStatus === 'closed_filled' || nextStatus === 'deleted'
      ? false
      : false);
  const normalizedStatus =
    patch.listingStatus ??
    (typeof patch.published === 'boolean'
      ? nextPublished
        ? 'published'
        : 'draft'
      : nextStatus);

  const updated: CareerJob = {
    ...existing,
    title: patch.title?.trim() ?? existing.title,
    requisitionId: patch.requisitionId?.trim() ?? existing.requisitionId,
    team: patch.team?.trim() ?? existing.team,
    brainNodeId:
      patch.brainNodeId !== undefined
        ? patch.brainNodeId?.trim() || undefined
        : existing.brainNodeId,
    location: patch.location?.trim() ?? existing.location,
    workType: patch.workType ?? existing.workType,
    summary: patch.summary?.trim() ?? existing.summary,
    description: patch.description?.trim() ?? existing.description,
    responsibilities: patch.responsibilities
      ? normalizeList(patch.responsibilities)
      : existing.responsibilities,
    requirements: patch.requirements ? normalizeList(patch.requirements) : existing.requirements,
    published: nextPublished,
    listingStatus: normalizedStatus,
    applicationPhases: normalizeApplicationPhases(
      patch.applicationPhases ?? existing.applicationPhases,
      existing.id,
    ),
    hiringManager:
      patch.hiringManager !== undefined
        ? patch.hiringManager.trim()
        : (existing.hiringManager ?? '').trim(),
    targetHireCount: normalizeTargetHireCount(
      patch.targetHireCount ?? existing.targetHireCount,
      1,
    ),
    compensation:
      patch.compensation !== undefined
        ? patch.compensation.trim()
        : (existing.compensation ?? '').trim(),
    updatedAt: Date.now(),
    source: 'admin',
  };

  const copy = [...current];
  if (idx >= 0) copy.splice(idx, 1);
  saveLocalCareerJobs([updated, ...copy]);
  return updated;
}

export function setLocalCareerJobListingStatus(
  jobId: string,
  listingStatus: JobListingStatus,
): CareerJob | null {
  const published = listingStatus === 'published';
  return updateLocalCareerJobRecord(jobId, { listingStatus, published });
}

export function deleteLocalCareerJobRecord(jobId: string): CareerJob | null {
  return setLocalCareerJobListingStatus(jobId, 'deleted');
}

export function getCareerJobBySlug(slug: string): CareerJob | null {
  const target = slug.trim().toLowerCase();
  if (!target) return null;
  const jobs = getPublishedCareerJobs();
  return jobs.find((job) => job.slug.toLowerCase() === target) ?? null;
}

export function getLocalCandidateRecords(): CandidateApplicationRecord[] {
  return readStorageArray<CandidateApplicationRecord>(LOCAL_CANDIDATES_KEY).filter(
    (record) => Boolean(record?.id && record?.email),
  ).map((record) => ({
    ...record,
    status: normalizeCandidateApplicationStatus(record.status),
    currentPhaseId: record.currentPhaseId ?? '',
    reviewer: record.reviewer ?? '',
    phaseNotes: record.phaseNotes ?? '',
    resumeStoragePath: record.resumeStoragePath ?? '',
    coverLetterStoragePath: record.coverLetterStoragePath ?? '',
    resumeDownloadUrl: record.resumeDownloadUrl ?? '',
    coverLetterDownloadUrl: record.coverLetterDownloadUrl ?? '',
    candidateCity: record.candidateCity ?? '',
    candidateState: record.candidateState ?? '',
    candidatePostalCode: record.candidatePostalCode ?? '',
    backgroundCheckAcknowledged: Boolean(record.backgroundCheckAcknowledged),
    phaseScorecard:
      typeof record.phaseScorecard === 'number' && Number.isFinite(record.phaseScorecard)
        ? Number(record.phaseScorecard)
        : null,
    offerAccepted: Boolean(record.offerAccepted),
    agentUserId:
      typeof record.agentUserId === 'string' && record.agentUserId.trim()
        ? record.agentUserId.trim()
        : null,
    applicationUpdatedAt:
      typeof record.applicationUpdatedAt === 'number' && Number.isFinite(record.applicationUpdatedAt)
        ? Number(record.applicationUpdatedAt)
        : record.updatedAt,
    updatedAt:
      typeof record.updatedAt === 'number' && Number.isFinite(record.updatedAt)
        ? Number(record.updatedAt)
        : Date.now(),
    createdAt:
      typeof record.createdAt === 'number' && Number.isFinite(record.createdAt)
        ? Number(record.createdAt)
        : Date.now(),
  }));
}

export function getCandidatesForCareerJob(
  job: Pick<CareerJob, 'id' | 'requisitionId'>,
): CandidateApplicationRecord[] {
  return getLocalCandidateRecords()
    .filter(
      (record) =>
        record.jobId === job.id ||
        record.requisitionId.toLowerCase() === job.requisitionId.toLowerCase(),
    )
    .sort((a, b) => b.updatedAt - a.updatedAt);
}

type CandidateApplicationLifecyclePatch = Partial<
  Pick<
    CandidateApplicationRecord,
    'status' | 'currentPhaseId' | 'reviewer' | 'phaseNotes' | 'phaseScorecard'
  >
>;

export function updateLocalCandidateApplicationLifecycle(
  uid: string,
  patch: CandidateApplicationLifecyclePatch,
): CandidateApplicationRecord | null {
  const records = getLocalCandidateRecords();
  const idVariants = new Set(resolveLocalCandidateIdVariants(uid));
  const idx = records.findIndex((record) => idVariants.has(record.id));
  if (idx < 0) return null;

  const current = records[idx];
  const updated: CandidateApplicationRecord = {
    ...current,
    status: normalizeCandidateApplicationStatus(patch.status ?? current.status),
    currentPhaseId: (patch.currentPhaseId ?? current.currentPhaseId ?? '').trim(),
    reviewer: (patch.reviewer ?? current.reviewer ?? '').trim(),
    phaseNotes: (patch.phaseNotes ?? current.phaseNotes ?? '').trim(),
    phaseScorecard:
      typeof patch.phaseScorecard === 'number' && Number.isFinite(patch.phaseScorecard)
        ? Number(patch.phaseScorecard)
        : current.phaseScorecard,
    applicationUpdatedAt: Date.now(),
    updatedAt: Date.now(),
  };

  const clone = [...records];
  clone[idx] = updated;
  saveLocalCandidateRecords(clone);
  return updated;
}

export function saveCareerJobFromBoard(job: CareerJob): CareerJob {
  const current = getLocalCareerJobs();
  const idx = current.findIndex((item) => item.id === job.id);
  const normalized: CareerJob = {
    ...job,
    listingStatus: getCareerJobListingStatus(job),
    applicationPhases: getCareerJobApplicationPhases(job),
    hiringManager: (job.hiringManager ?? '').trim(),
    targetHireCount: normalizeTargetHireCount(job.targetHireCount, 1),
    compensation: (job.compensation ?? '').trim(),
    updatedAt: Date.now(),
    source: 'admin',
  };

  if (idx < 0) {
    saveLocalCareerJobs([normalized, ...current]);
    return normalized;
  }

  const clone = [...current];
  clone.splice(idx, 1);
  saveLocalCareerJobs([normalized, ...clone]);
  return normalized;
}

export function getCareerJobById(jobId: string): CareerJob | null {
  return getAllCareerJobs().find((job) => job.id === jobId) ?? null;
}

export function getActiveCareerJobsForBoard(): CareerJob[] {
  return getAllCareerJobs().filter((job) => getCareerJobListingStatus(job) !== 'deleted');
}

export function saveCandidateRecord(record: CandidateApplicationRecord): CandidateApplicationRecord {
  const records = getLocalCandidateRecords();
  const idx = records.findIndex((item) => item.id === record.id);
  const normalized: CandidateApplicationRecord = {
    ...record,
    status: normalizeCandidateApplicationStatus(record.status),
    currentPhaseId: record.currentPhaseId ?? '',
    reviewer: record.reviewer ?? '',
    phaseNotes: record.phaseNotes ?? '',
    resumeStoragePath: (record.resumeStoragePath ?? '').trim(),
    coverLetterStoragePath: (record.coverLetterStoragePath ?? '').trim(),
    resumeDownloadUrl: (record.resumeDownloadUrl ?? '').trim(),
    coverLetterDownloadUrl: (record.coverLetterDownloadUrl ?? '').trim(),
    candidateCity: (record.candidateCity ?? '').trim(),
    candidateState: (record.candidateState ?? '').trim(),
    candidatePostalCode: (record.candidatePostalCode ?? '').trim(),
    backgroundCheckAcknowledged: Boolean(record.backgroundCheckAcknowledged),
    phaseScorecard:
      typeof record.phaseScorecard === 'number' && Number.isFinite(record.phaseScorecard)
        ? Number(record.phaseScorecard)
        : null,
    offerAccepted: Boolean(record.offerAccepted),
    agentUserId:
      typeof record.agentUserId === 'string' && record.agentUserId.trim()
        ? record.agentUserId.trim()
        : null,
    applicationUpdatedAt: Date.now(),
    updatedAt: Date.now(),
  };

  if (idx < 0) {
    saveLocalCandidateRecords([normalized, ...records]);
    return normalized;
  }
  const copy = [...records];
  copy[idx] = normalized;
  saveLocalCandidateRecords(copy);
  return normalized;
}

export function saveLocalCandidateRecords(records: CandidateApplicationRecord[]): void {
  writeStorageArray(LOCAL_CANDIDATES_KEY, records);
}

export function isLocalCandidateId(uid: string | null | undefined): boolean {
  if (!uid) return false;
  return uid.startsWith('candidate_') || uid.startsWith('career_candidate_');
}

function resolveLocalCandidateIdVariants(uid: string): string[] {
  const trimmed = uid.trim();
  if (!trimmed) return [];
  if (trimmed.startsWith('candidate_')) {
    const suffix = trimmed.substring('candidate_'.length);
    return Array.from(new Set([trimmed, `career_candidate_${suffix}`]));
  }
  if (trimmed.startsWith('career_candidate_')) {
    const suffix = trimmed.substring('career_candidate_'.length);
    return Array.from(new Set([trimmed, `candidate_${suffix}`]));
  }
  return [trimmed];
}

export function createLocalCandidateRecord(
  draft: CandidateApplicationDraft,
): CandidateApplicationRecord {
  const now = Date.now();
  const records = getLocalCandidateRecords();
  const firstName = draft.firstName.trim();
  const lastName = draft.lastName.trim();
  const combinedName = `${firstName} ${lastName}`.trim();
  const name = combinedName || draft.email.trim();
  const status = normalizeCandidateApplicationStatus(draft.status);
  const currentPhaseId = (draft.currentPhaseId ?? '').trim();

  const record: CandidateApplicationRecord = {
    id: `candidate_${now}_${Math.random().toString(36).slice(2, 7)}`,
    role: 'candidate',
    firstName,
    lastName,
    name,
    email: draft.email.trim(),
    contactPhone: (draft.contactPhone ?? '').trim(),
    userType: draft.userType,
    businessName: (draft.businessName ?? '').trim(),
    businessWebsite: (draft.businessWebsite ?? '').trim(),
    businessAddress: (draft.businessAddress ?? '').trim(),
    candidateCity: (draft.candidateCity ?? '').trim(),
    candidateState: (draft.candidateState ?? '').trim(),
    candidatePostalCode: (draft.candidatePostalCode ?? '').trim(),
    backgroundCheckAcknowledged: Boolean(draft.backgroundCheckAcknowledged),
    pointOfContact: (draft.pointOfContact ?? '').trim(),
    bio: (draft.bio ?? '').trim(),
    linkedInUrl: (draft.linkedInUrl ?? '').trim(),
    portfolioUrl: (draft.portfolioUrl ?? '').trim(),
    coverLetterText: (draft.coverLetterText ?? '').trim(),
    requisitionId: draft.requisitionId.trim(),
    jobId: draft.jobId.trim(),
    jobTitle: draft.jobTitle.trim(),
    resumeFileName: (draft.resumeFileName ?? '').trim(),
    coverLetterFileName: (draft.coverLetterFileName ?? '').trim(),
    resumeStoragePath: (draft.resumeStoragePath ?? '').trim(),
    coverLetterStoragePath: (draft.coverLetterStoragePath ?? '').trim(),
    resumeDownloadUrl: (draft.resumeDownloadUrl ?? '').trim(),
    coverLetterDownloadUrl: (draft.coverLetterDownloadUrl ?? '').trim(),
    source: draft.source ?? 'job_board',
    status,
    currentPhaseId,
    reviewer: (draft.reviewer ?? '').trim(),
    phaseNotes: (draft.phaseNotes ?? '').trim(),
    phaseScorecard:
      typeof draft.phaseScorecard === 'number' && Number.isFinite(draft.phaseScorecard)
        ? Number(draft.phaseScorecard)
        : null,
    offerAccepted: Boolean(draft.offerAccepted),
    agentUserId:
      typeof draft.agentUserId === 'string' && draft.agentUserId.trim()
        ? draft.agentUserId.trim()
        : null,
    applicationUpdatedAt: now,
    createdAt: now,
    updatedAt: now,
  };

  saveLocalCandidateRecords([record, ...records]);
  return record;
}

export function getLocalCandidateById(uid: string): CandidateApplicationRecord | null {
  const records = getLocalCandidateRecords();
  const idVariants = new Set(resolveLocalCandidateIdVariants(uid));
  return records.find((record) => idVariants.has(record.id)) ?? null;
}

export function updateLocalCandidateRecord(
  uid: string,
  patch: Partial<
    Pick<
      CandidateApplicationRecord,
      | 'firstName'
      | 'lastName'
      | 'userType'
      | 'contactPhone'
      | 'businessName'
      | 'businessAddress'
      | 'businessWebsite'
      | 'candidateCity'
      | 'candidateState'
      | 'candidatePostalCode'
      | 'backgroundCheckAcknowledged'
      | 'pointOfContact'
      | 'bio'
    >
  >,
): CandidateApplicationRecord | null {
  const records = getLocalCandidateRecords();
  const idVariants = new Set(resolveLocalCandidateIdVariants(uid));
  const idx = records.findIndex((record) => idVariants.has(record.id));
  if (idx < 0) return null;

  const current = records[idx];
  const nextFirst = (patch.firstName ?? current.firstName).trim();
  const nextLast = (patch.lastName ?? current.lastName).trim();
  const nextName = `${nextFirst} ${nextLast}`.trim() || current.email;
  const nextUserType =
    patch.userType === 'business' || patch.userType === 'individual'
      ? patch.userType
      : current.userType;

  const updated: CandidateApplicationRecord = {
    ...current,
    firstName: nextFirst,
    lastName: nextLast,
    name: nextName,
    userType: nextUserType,
    contactPhone: (patch.contactPhone ?? current.contactPhone).trim(),
    businessName: (patch.businessName ?? current.businessName).trim(),
    businessAddress: (patch.businessAddress ?? current.businessAddress).trim(),
    businessWebsite: (patch.businessWebsite ?? current.businessWebsite).trim(),
    candidateCity: (patch.candidateCity ?? current.candidateCity).trim(),
    candidateState: (patch.candidateState ?? current.candidateState).trim(),
    candidatePostalCode: (patch.candidatePostalCode ?? current.candidatePostalCode).trim(),
    backgroundCheckAcknowledged:
      typeof patch.backgroundCheckAcknowledged === 'boolean'
        ? patch.backgroundCheckAcknowledged
        : current.backgroundCheckAcknowledged,
    pointOfContact: (patch.pointOfContact ?? current.pointOfContact).trim(),
    bio: (patch.bio ?? current.bio).trim(),
    updatedAt: Date.now(),
  };

  const clone = [...records];
  clone[idx] = updated;
  saveLocalCandidateRecords(clone);
  return updated;
}

export function deleteLocalCandidateRecord(uid: string): boolean {
  const records = getLocalCandidateRecords();
  const idVariants = new Set(resolveLocalCandidateIdVariants(uid));
  const next = records.filter((record) => !idVariants.has(record.id));
  if (next.length === records.length) return false;
  saveLocalCandidateRecords(next);
  return true;
}

export function deleteLocalCandidateRecordsByEmail(email: string): number {
  const normalized = email.trim().toLowerCase();
  if (!normalized) return 0;
  const records = getLocalCandidateRecords();
  const next = records.filter((record) => record.email.trim().toLowerCase() !== normalized);
  const deletedCount = records.length - next.length;
  if (deletedCount > 0) {
    saveLocalCandidateRecords(next);
  }
  return deletedCount;
}

export function candidateRecordToUserRecord(record: CandidateApplicationRecord): UserRecord {
  return {
    id: record.id,
    email: record.email,
    name: record.name,
    businessName: record.businessName || null,
    role: 'candidate',
    banned: false,
    online: false,
    lastActiveAt: record.updatedAt,
  };
}

export function getCandidateUserRecords(): UserRecord[] {
  return getLocalCandidateRecords().map(candidateRecordToUserRecord);
}

export function candidateRecordToUserDetails(record: CandidateApplicationRecord): Record<string, unknown> {
  return {
    id: record.id,
    role: 'candidate',
    name: record.name,
    firstName: record.firstName,
    lastName: record.lastName,
    email: record.email,
    userType: record.userType,
    contactPhone: record.contactPhone,
    businessName: record.businessName,
    businessAddress: record.businessAddress,
    candidateCity: record.candidateCity,
    candidateState: record.candidateState,
    candidatePostalCode: record.candidatePostalCode,
    backgroundCheckAcknowledged: record.backgroundCheckAcknowledged,
    businessWebsite: record.businessWebsite,
    pointOfContact: record.pointOfContact || record.name,
    bio: record.bio,
    linkedInUrl: record.linkedInUrl,
    portfolioWebsite: record.portfolioUrl,
    requisitionId: record.requisitionId,
    jobId: record.jobId,
    jobTitle: record.jobTitle,
    resumeFileName: record.resumeFileName,
    coverLetterFileName: record.coverLetterFileName,
    resumeStoragePath: record.resumeStoragePath,
    coverLetterStoragePath: record.coverLetterStoragePath,
    resumeDownloadUrl: record.resumeDownloadUrl,
    coverLetterDownloadUrl: record.coverLetterDownloadUrl,
    leadCaptureSource: record.source,
    applicationStatus: record.status,
    applicationPhaseId: record.currentPhaseId,
    applicationReviewer: record.reviewer,
    applicationPhaseNotes: record.phaseNotes,
    applicationPhaseScorecard: record.phaseScorecard,
    applicationStageReviewerScores: record.stageReviewerScores ?? {},
    interviewStatus: record.interviewStatus ?? 'not_scheduled',
    interviewScheduledAt: record.interviewScheduledAt ?? null,
    interviewCompletedAt: record.interviewCompletedAt ?? null,
    interviewMeetingUrl: record.interviewMeetingUrl ?? '',
    latestInterviewEventId: record.latestInterviewEventId ?? null,
    applicationUpdatedAt: record.applicationUpdatedAt,
    createdAt: record.createdAt,
    updatedAt: record.updatedAt,
  };
}
